import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { useDarkMode } from "../context/DarkModeContext";
import ScarcityCounter from "../components/ScarcityCounter";
import VelvetRope from "../components/VelvetRope";
import PriorityCheckoutModal from "../components/PriorityCheckoutModal";

// ============================================================================
// GauriLabs · ICE_Man — "Scroll = Execution" landing
// Self-contained: own minimal nav, gold/monochrome, Fraunces serif display.
// One WebGL corridor the camera flies through as you scroll.
// three.js loaded from CDN at runtime (no npm), with graceful fallback.
// ============================================================================

var THREE_URL = "https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.min.js";
var FONT_URL =
  "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;0,9..144,500;0,9..144,600;1,9..144,400;1,9..144,500&family=Sora:wght@300;400;500;600&family=Space+Mono:wght@400;700&display=swap";

// Inject fonts + scoped styles once
function useLandingStyles() {
  useEffect(function () {
    if (!document.getElementById("gl-fonts-fraunces")) {
      var l = document.createElement("link");
      l.id = "gl-fonts-fraunces";
      l.rel = "stylesheet";
      l.href = FONT_URL;
      document.head.appendChild(l);
    }
    if (!document.getElementById("gl-landing-css")) {
      var s = document.createElement("style");
      s.id = "gl-landing-css";
      s.innerHTML = CSS;
      document.head.appendChild(s);
    }
  }, []);
}

var CSS = [
  ".glx{--gold:#C9A24E;--gold-hi:#E7CE92;--gold-lo:#8F7328;--bg:#08070A;--ink:#0D0C10;",
  "--t1:#F3F0E9;--t2:#9A968D;--t3:#5C594F;--line:rgba(243,240,233,0.09);--line2:rgba(243,240,233,0.18);",
  "--disp:'Fraunces',Georgia,serif;--body:'Sora',system-ui,sans-serif;--mono:'Space Mono',monospace;}",
  ".glx.light{--bg:#F6F3EC;--ink:#FFFFFF;--t1:#0D0B08;--t2:#57534A;--t3:#8B867B;",
  "--line:rgba(13,11,8,0.10);--line2:rgba(13,11,8,0.2);--gold:#A47E29;--gold-hi:#C79E42;--gold-lo:#6E5417;}",
  ".glx{background:var(--bg);color:var(--t1);font-family:var(--body);-webkit-font-smoothing:antialiased;overflow-x:hidden;position:relative;transition:background .4s,color .4s;min-height:100vh;}",
  ".glx *::selection{background:var(--gold);color:#08070A;}",
  ".glx .mono{font-family:var(--mono);}",
  ".glx a{color:inherit;text-decoration:none;}",
  ".glx-stage{position:fixed;inset:0;z-index:0;}",
  ".glx-grain{position:fixed;inset:0;z-index:1;pointer-events:none;opacity:.045;mix-blend-mode:overlay;background-image:url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\");}",
  ".glx-rail{position:fixed;top:0;left:0;height:2px;background:linear-gradient(90deg,var(--gold-lo),var(--gold-hi));z-index:40;width:0;box-shadow:0 0 12px var(--gold);}",
  ".glx-chapters{position:fixed;right:26px;top:50%;transform:translateY(-50%);z-index:40;display:flex;flex-direction:column;gap:16px;}",
  ".glx-chapters button{width:8px;height:8px;border-radius:50%;border:1px solid var(--line2);background:transparent;cursor:pointer;padding:0;transition:all .2s;}",
  ".glx-chapters button.on{background:var(--gold);border-color:var(--gold);transform:scale(1.4);box-shadow:0 0 10px var(--gold);}",
  "@media(max-width:760px){.glx-chapters{display:none;}}",
  ".glx-nav{position:fixed;top:0;left:0;right:0;z-index:30;display:flex;align-items:center;justify-content:space-between;padding:24px 32px;mix-blend-mode:difference;}",
  ".glx-brand{display:flex;align-items:center;gap:11px;color:#fff;}",
  ".glx-brand .nm{font-family:var(--disp);font-weight:500;font-size:22px;letter-spacing:-.01em;}",
  ".glx-navr{display:flex;gap:22px;align-items:center;color:#fff;font-family:var(--mono);font-size:12px;}",
  ".glx-navr a{opacity:.82;}.glx-navr a:hover{opacity:1;}",
  ".glx-enter{border:1px solid #fff;padding:8px 16px;border-radius:2px;}",
  ".glx-content{position:relative;z-index:2;pointer-events:none;}",
  ".glx-chap{min-height:100vh;display:flex;flex-direction:column;justify-content:center;padding:0 32px;max-width:1200px;margin:0 auto;}",
  ".glx-chap *{pointer-events:auto;}",
  ".glx-kick{font-family:var(--mono);font-size:12px;letter-spacing:.24em;text-transform:uppercase;color:var(--gold);margin-bottom:22px;}",
  ".glx-chap h2{font-family:var(--disp);font-weight:400;line-height:0.94;letter-spacing:-.03em;font-size:clamp(46px,9vw,150px);}",
  ".glx-chap h2 em{font-style:italic;color:var(--gold);}",
  ".glx-chap h2 .stroke{color:transparent;-webkit-text-stroke:1px var(--t2);}",
  ".glx-say{max-width:44ch;font-size:18px;color:var(--t2);margin-top:26px;line-height:1.6;}",
  ".glx-c0 h1{font-family:var(--disp);font-weight:400;font-size:clamp(70px,15vw,240px);line-height:0.9;letter-spacing:-.04em;}",
  ".glx-c0 h1 em{font-style:italic;color:var(--gold);}",
  ".glx-c0 .row{display:flex;align-items:flex-end;justify-content:space-between;gap:30px;flex-wrap:wrap;margin-top:36px;}",
  ".glx-c0 .lede{max-width:40ch;font-size:18px;color:var(--t2);}",
  ".glx-cta{display:flex;gap:12px;flex-wrap:wrap;}",
  ".glx-btn{font-family:var(--mono);font-size:12.5px;padding:15px 26px;border-radius:2px;cursor:pointer;border:1px solid var(--line2);display:inline-flex;gap:9px;align-items:center;transition:all .16s;background:transparent;color:var(--t1);}",
  ".glx-btn-p{background:var(--gold);color:#08070A;border-color:var(--gold);}",
  ".glx-btn-p:hover{background:var(--gold-hi);border-color:var(--gold-hi);}",
  ".glx-btn-g:hover{border-color:var(--gold);color:var(--gold);}",
  ".glx-prompt{font-family:var(--mono);font-size:clamp(18px,3vw,32px);color:var(--t1);border-left:2px solid var(--gold);padding:14px 0 14px 26px;max-width:22ch;line-height:1.5;}",
  ".glx-prompt .cur{display:inline-block;width:.5ch;background:var(--gold);animation:glxblink 1s steps(1) infinite;}",
  "@keyframes glxblink{50%{opacity:0;}}",
  ".glx-confirm{border:1px solid var(--line2);background:color-mix(in srgb,var(--ink) 82%,transparent);backdrop-filter:blur(10px);border-radius:8px;padding:28px;max-width:430px;font-family:var(--mono);font-size:13px;}",
  ".glx-confirm .r{display:flex;justify-content:space-between;padding:9px 0;border-top:1px solid var(--line);}",
  ".glx-confirm .r:first-of-type{border-top:none;}",
  ".glx-confirm .ok{color:var(--gold);}",
  ".glx-confirm .hd{color:var(--gold);letter-spacing:.14em;text-transform:uppercase;font-size:11px;margin-bottom:14px;}",
  ".glx-solid{position:relative;z-index:2;border-top:1px solid var(--line);}",
  ".glx-wrap{max-width:1180px;margin:0 auto;padding:0 32px;}",
  ".glx-sec{padding:110px 0;border-bottom:1px solid var(--line);background:color-mix(in srgb,var(--bg) 82%,transparent);backdrop-filter:blur(3px);}",
  ".glx-sec.glx-last{background:var(--bg);}",
  ".glx-sectop{display:flex;align-items:baseline;gap:16px;margin-bottom:48px;}",
  ".glx-sectop .n{font-family:var(--mono);font-size:12px;color:var(--gold);}",
  ".glx-sectop h3{font-family:var(--disp);font-weight:400;font-size:clamp(30px,5vw,58px);line-height:0.96;letter-spacing:-.03em;}",
  ".glx-sectop h3 em{font-style:italic;color:var(--gold);}",
  ".glx-dark-band{background:var(--ink);border-bottom:1px solid var(--line);}",
  ".glx-tiers{display:grid;grid-template-columns:1fr 1fr;gap:16px;max-width:820px;}",
  "@media(max-width:640px){.glx-tiers{grid-template-columns:1fr;}}",
  ".glx-tier{border:1px solid var(--line);border-radius:5px;padding:36px;background:var(--ink);position:relative;transition:all .18s;}",
  ".glx-tier:hover{transform:translateY(-4px);border-color:var(--line2);}",
  ".glx-tier.pop{border-color:color-mix(in srgb,var(--gold) 55%,transparent);}",
  ".glx-tier .badge{position:absolute;top:-12px;left:30px;background:var(--gold);color:#08070A;font-family:var(--mono);font-size:10px;letter-spacing:.06em;text-transform:uppercase;padding:5px 12px;border-radius:3px;}",
  ".glx-tier .pn{font-family:var(--disp);font-weight:500;font-size:28px;letter-spacing:-.01em;}",
  ".glx-tier .pr{font-family:var(--disp);font-weight:400;font-size:60px;letter-spacing:-.03em;margin:14px 0 3px;}",
  ".glx-tier .pr small{font-family:var(--mono);font-size:14px;color:var(--t3);}",
  ".glx-tier .desc{font-size:13px;color:var(--t3);margin-bottom:22px;}",
  ".glx-tier .ft{display:flex;gap:11px;font-size:14px;color:var(--t2);padding:10px 0;border-top:1px solid var(--line);}",
  ".glx-tier .ft:first-of-type{border-top:none;}",
  ".glx-tier .ck{color:var(--gold);}",
  ".glx-tier .cta{margin-top:26px;width:100%;justify-content:center;}",
  ".glx-foot{position:relative;z-index:2;background:var(--ink);padding:62px 0 42px;border-top:1px solid var(--line);}",
  ".glx-fgrid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr;gap:34px;margin-bottom:42px;}",
  "@media(max-width:760px){.glx-fgrid{grid-template-columns:1fr 1fr;}}",
  ".glx-fgrid h4{font-family:var(--mono);font-size:10.5px;letter-spacing:.12em;text-transform:uppercase;color:var(--t3);margin-bottom:15px;}",
  ".glx-fgrid a{display:block;font-size:13.5px;color:var(--t2);margin-bottom:10px;}",
  ".glx-fgrid a:hover{color:var(--gold);}",
  ".glx-flede{font-size:14px;color:var(--t2);max-width:30ch;margin-top:13px;}",
  ".glx-fbot{display:flex;justify-content:space-between;padding-top:26px;border-top:1px solid var(--line);font-family:var(--mono);font-size:11px;color:var(--t3);gap:12px;flex-wrap:wrap;}",
  ".glx-mode{position:fixed;bottom:20px;left:20px;z-index:40;width:38px;height:38px;border:1px solid var(--line2);border-radius:50%;background:color-mix(in srgb,var(--bg) 60%,transparent);backdrop-filter:blur(6px);color:var(--t2);cursor:pointer;display:flex;align-items:center;justify-content:center;}",
  ".glx-mode:hover{color:var(--gold);border-color:var(--gold);}",
].join("");

// ── WebGL corridor engine (returns a cleanup fn) ──
function startEngine(canvas, railEl, dotEls, chapEls, getForceLight) {
  var THREE = window.THREE;
  if (!THREE) return function () {};
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var alive = true;

  function cv(name, fallback) {
    var v = getComputedStyle(canvas.parentElement).getPropertyValue(name).trim();
    return v || fallback;
  }
  function C() {
    return {
      g: new THREE.Color(cv("--gold", "#C9A24E")),
      ghi: new THREE.Color(cv("--gold-hi", "#E7CE92")),
      t1: new THREE.Color(cv("--t1", "#F3F0E9")),
      t3: new THREE.Color(cv("--t3", "#5C594F")),
      bg: new THREE.Color(cv("--bg", "#08070A")),
    };
  }

  var scene = new THREE.Scene();
  var cam = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 200);
  var r = new THREE.WebGLRenderer({ canvas: canvas, alpha: true, antialias: true });
  r.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  r.setSize(window.innerWidth, window.innerHeight, false);

  var col = C();
  scene.fog = new THREE.FogExp2(col.bg, 0.028);
  var world = new THREE.Group();
  scene.add(world);

  function glowTex() {
    var c = document.createElement("canvas");
    c.width = c.height = 128;
    var g = c.getContext("2d");
    var rg = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    rg.addColorStop(0, "rgba(255,255,255,1)");
    rg.addColorStop(0.4, "rgba(255,255,255,.4)");
    rg.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = rg;
    g.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
  }
  var GT = glowTex();
  function station(z) { var g = new THREE.Group(); g.position.z = z; world.add(g); return g; }

  // 0 seed
  var s0 = station(0);
  var seed = new THREE.Mesh(new THREE.IcosahedronGeometry(1.6, 1), new THREE.MeshBasicMaterial({ color: col.g, wireframe: true, transparent: true, opacity: 0.5 }));
  s0.add(seed);
  var seedGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: GT, color: col.g, transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending }));
  seedGlow.scale.set(7, 7, 1); s0.add(seedGlow);

  // 1 command tunnel
  var s1 = station(-30);
  var wc = 520, wpos = new Float32Array(wc * 3), whome = new Float32Array(wc * 3);
  for (var i = 0; i < wc; i++) {
    var a = Math.random() * Math.PI * 2, rr = 0.4 + Math.random() * 2.4, zz = (Math.random() - 0.5) * 10;
    var x = Math.cos(a) * rr, y = Math.sin(a) * rr;
    wpos[i * 3] = x; wpos[i * 3 + 1] = y; wpos[i * 3 + 2] = zz;
    whome[i * 3] = x; whome[i * 3 + 1] = y; whome[i * 3 + 2] = zz;
  }
  var wg = new THREE.BufferGeometry(); wg.setAttribute("position", new THREE.BufferAttribute(wpos, 3));
  var wm = new THREE.PointsMaterial({ color: col.t1, size: 0.038, transparent: true, opacity: 0.7 });
  var wave = new THREE.Points(wg, wm); s1.add(wave);

  // 2 claude rings
  var s2 = station(-60);
  var tor1 = new THREE.Mesh(new THREE.TorusGeometry(2, 0.035, 12, 120), new THREE.MeshBasicMaterial({ color: col.t1, transparent: true, opacity: 0.5 })); s2.add(tor1);
  var tor2 = new THREE.Mesh(new THREE.TorusGeometry(2, 0.035, 12, 120), new THREE.MeshBasicMaterial({ color: col.g, transparent: true, opacity: 0.45 })); tor2.rotation.x = Math.PI / 2; s2.add(tor2);
  var tor3 = new THREE.Mesh(new THREE.TorusGeometry(2, 0.035, 12, 120), new THREE.MeshBasicMaterial({ color: col.ghi, transparent: true, opacity: 0.35 })); tor3.rotation.y = Math.PI / 2; s2.add(tor3);

  // 3 iceman fan
  var s3 = station(-95);
  var icore = new THREE.Mesh(new THREE.SphereGeometry(0.9, 32, 32), new THREE.MeshBasicMaterial({ color: col.g })); s3.add(icore);
  var icoreGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: GT, color: col.g, transparent: true, opacity: 0.6, depthWrite: false, blending: THREE.AdditiveBlending }));
  icoreGlow.scale.set(6, 6, 1); s3.add(icoreGlow);
  var fan = new THREE.Group(); s3.add(fan);
  for (var k = 0; k < 12; k++) {
    var ang = (k / 12) * Math.PI * 2;
    var tgt = new THREE.Vector3(Math.cos(ang) * 3.2, Math.sin(ang) * 3.2, (Math.random() - 0.5) * 2);
    var lg = new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), tgt]);
    fan.add(new THREE.Line(lg, new THREE.LineBasicMaterial({ color: col.ghi, transparent: true, opacity: 0.4 })));
    var nb = new THREE.Mesh(new THREE.SphereGeometry(0.11, 16, 16), new THREE.MeshBasicMaterial({ color: col.t1 }));
    nb.position.copy(tgt); fan.add(nb);
  }

  // 4 solana ring
  var s4 = station(-130);
  var ring = new THREE.Mesh(new THREE.TorusGeometry(2.2, 0.07, 16, 120), new THREE.MeshBasicMaterial({ color: col.g })); s4.add(ring);
  var ring2 = new THREE.Mesh(new THREE.TorusGeometry(1.5, 0.025, 12, 100), new THREE.MeshBasicMaterial({ color: col.t1, transparent: true, opacity: 0.5 })); s4.add(ring2);
  var check = new THREE.Sprite(new THREE.SpriteMaterial({ map: GT, color: col.g, transparent: true, opacity: 0.7, depthWrite: false, blending: THREE.AdditiveBlending }));
  check.scale.set(5, 5, 1); s4.add(check);

  // extended flight: drifting fields + faint rings + final landing core
  var driftMeshes = [];
  for (var d = 0; d < 5; d++) {
    var dz = -165 - d * 38;
    var gd = station(dz);
    var fr = new THREE.Mesh(new THREE.TorusGeometry(2.6 + (d % 2) * 0.6, 0.02, 10, 90), new THREE.MeshBasicMaterial({ color: (d % 2 ? col.g : col.t1), transparent: true, opacity: 0.18 }));
    fr.rotation.x = Math.random() * Math.PI; fr.rotation.y = Math.random() * Math.PI; gd.add(fr);
    driftMeshes.push(fr);
    var dc = 160, dp = new Float32Array(dc * 3);
    for (var q = 0; q < dc; q++) { dp[q * 3] = (Math.random() - 0.5) * 9; dp[q * 3 + 1] = (Math.random() - 0.5) * 9; dp[q * 3 + 2] = (Math.random() - 0.5) * 20; }
    var dg = new THREE.BufferGeometry(); dg.setAttribute("position", new THREE.BufferAttribute(dp, 3));
    gd.add(new THREE.Points(dg, new THREE.PointsMaterial({ color: col.t3, size: 0.03, transparent: true, opacity: 0.4 })));
  }
  var sEnd = station(-330);
  var endCore = new THREE.Mesh(new THREE.IcosahedronGeometry(1.3, 1), new THREE.MeshBasicMaterial({ color: col.g, wireframe: true, transparent: true, opacity: 0.4 })); sEnd.add(endCore);
  var endGlow = new THREE.Sprite(new THREE.SpriteMaterial({ map: GT, color: col.g, transparent: true, opacity: 0.5, depthWrite: false, blending: THREE.AdditiveBlending }));
  endGlow.scale.set(9, 9, 1); sEnd.add(endGlow);

  function recolor() {
    var c = C();
    scene.fog.color = c.bg;
    seed.material.color = c.g; seedGlow.material.color = c.g; wm.color = c.t1;
    tor1.material.color = c.t1; tor2.material.color = c.g; tor3.material.color = c.ghi;
    icore.material.color = c.g; icoreGlow.material.color = c.g;
    ring.material.color = c.g; ring2.material.color = c.t1; check.material.color = c.g;
    endCore.material.color = c.g; endGlow.material.color = c.g;
  }

  var px = 0, py = 0, tms = 0, camZ = 8;
  function onMove(e) { px = (e.clientX / window.innerWidth) * 2 - 1; py = (e.clientY / window.innerHeight) * 2 - 1; }
  function onResize() { cam.aspect = window.innerWidth / window.innerHeight; cam.updateProjectionMatrix(); r.setSize(window.innerWidth, window.innerHeight, false); }
  window.addEventListener("mousemove", onMove);
  window.addEventListener("resize", onResize);

  function progress() {
    var end = document.body.scrollHeight - window.innerHeight;
    return end > 0 ? Math.max(0, Math.min(1, window.scrollY / end)) : 0;
  }

  var io = new IntersectionObserver(function (es) {
    es.forEach(function (e) {
      if (e.isIntersecting) {
        var idx = +e.target.getAttribute("data-chap");
        dotEls.forEach(function (d, i) { d.classList.toggle("on", i === idx); });
      }
    });
  }, { threshold: 0.5 });
  chapEls.forEach(function (c) { io.observe(c); });

  var lastForce = getForceLight();
  (function loop() {
    if (!alive) return;
    requestAnimationFrame(loop);
    tms += 0.016;
    if (getForceLight() !== lastForce) { lastForce = getForceLight(); recolor(); }
    var p = progress();
    if (railEl) railEl.style.width = p * 100 + "%";
    var targetZ = 8 - p * 346;
    camZ += (targetZ - camZ) * 0.08;
    cam.position.z = camZ;
    cam.position.x += (px * 1.2 - cam.position.x) * 0.04;
    cam.position.y += (-py * 0.8 - cam.position.y) * 0.04;
    cam.lookAt(0, 0, camZ - 6);
    if (!reduce) {
      seed.rotation.y += 0.004; seed.rotation.x += 0.002;
      var wa = wave.geometry.attributes.position.array;
      for (var i2 = 0; i2 < wc; i2++) { var kk = i2 * 3; wa[kk + 2] = whome[kk + 2] + Math.sin(tms * 2 + i2 * 0.1) * 0.4; }
      wave.geometry.attributes.position.needsUpdate = true; wave.rotation.z += 0.002;
      tor1.rotation.z += 0.01; tor2.rotation.y += 0.012; tor3.rotation.x += 0.011;
      icore.rotation.y += 0.01; fan.rotation.z += 0.006;
      ring.rotation.z += 0.008; ring2.rotation.z -= 0.014; ring2.rotation.x += 0.01;
      for (var m = 0; m < driftMeshes.length; m++) { driftMeshes[m].rotation.z += 0.003 + m * 0.0007; driftMeshes[m].rotation.x += 0.002; }
      endCore.rotation.y += 0.005; endCore.rotation.x += 0.003;
    }
    seedGlow.material.opacity = 0.5 + Math.sin(tms * 1.6) * 0.15;
    icoreGlow.material.opacity = 0.5 + Math.sin(tms * 1.8) * 0.2;
    check.material.opacity = 0.6 + Math.sin(tms * 2.2) * 0.2;
    endGlow.material.opacity = 0.4 + Math.sin(tms * 1.3) * 0.15;
    r.render(scene, cam);
  })();

  return function cleanup() {
    alive = false;
    window.removeEventListener("mousemove", onMove);
    window.removeEventListener("resize", onResize);
    io.disconnect();
    try { r.forceContextLoss(); } catch (e) {}
    try { r.dispose(); } catch (e) {}
  };
}

export default function Landing() {
  useLandingStyles();
  useTheme(); // kept for provider compatibility; visuals are fixed gold/mono
  var dm = useDarkMode();
  var isDark = dm.isDark;
  var toggleMode = dm.toggleMode;
  var navigate = useNavigate();

  var [priorityOpen, setPriorityOpen] = useState(false);
  var stageRef = useRef(null);
  var railRef = useRef(null);
  var chaptersRef = useRef(null);

  var goTier = function (tier) { navigate("/login?tier=" + tier); };

  // Build chapter dots + start engine after three.js loads
  useEffect(function () {
    var cleanup = null;
    var chapEls = Array.prototype.slice.call(document.querySelectorAll(".glx-chap"));
    // create dots
    var dotEls = [];
    if (chaptersRef.current) {
      chaptersRef.current.innerHTML = "";
      chapEls.forEach(function (c) {
        var b = document.createElement("button");
        b.addEventListener("click", function () { c.scrollIntoView({ behavior: "smooth" }); });
        chaptersRef.current.appendChild(b);
        dotEls.push(b);
      });
    }
    function getForceLight() { return !isDarkRef.current; }

    function begin() {
      if (stageRef.current) cleanup = startEngine(stageRef.current, railRef.current, dotEls, chapEls, getForceLight);
    }
    if (window.THREE) begin();
    else {
      var existing = document.getElementById("gl-three-cdn");
      if (existing) { existing.addEventListener("load", begin); }
      else {
        var s = document.createElement("script");
        s.id = "gl-three-cdn"; s.src = THREE_URL;
        s.onload = begin; s.onerror = function () {};
        document.head.appendChild(s);
      }
    }
    return function () { if (cleanup) cleanup(); };
    // eslint-disable-next-line
  }, []);

  // keep engine aware of current mode without re-mounting
  var isDarkRef = useRef(isDark);
  useEffect(function () { isDarkRef.current = isDark; }, [isDark]);

  return (
    <div className={"glx" + (isDark ? "" : " light")}>
      <canvas className="glx-stage" ref={stageRef} aria-label="ICE_Man execution journey" />
      <div className="glx-grain" />
      <div className="glx-rail" ref={railRef} />
      <div className="glx-chapters" ref={chaptersRef} />

      {/* minimal nav */}
      <nav className="glx-nav">
        <Link to="/" className="glx-brand">
          <svg width="26" height="26" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="#fff" strokeWidth="1.3" /><circle cx="20" cy="20" r="4" fill="#fff" /></svg>
          <span className="nm">GauriLabs</span>
        </Link>
        <div className="glx-navr">
          <a href="#glx-access">Access</a>
          <a href="#glx-access" className="glx-enter">Enter</a>
        </div>
      </nav>

      {/* JOURNEY */}
      <div className="glx-content">
        <section className="glx-chap glx-c0" data-chap="0">
          <div className="glx-kick">GauriLabs · ICE_Man</div>
          <h1>Speak.<br /><em>It trades.</em></h1>
          <div className="row">
            <p className="lede">One sentence to Claude.ai. ICE_Man turns it into a signed, settled trade on Solana. Watch it happen — scroll.</p>
            <div className="glx-cta">
              <a className="glx-btn glx-btn-p" href="#glx-access">Enter the grid →</a>
              <a className="glx-btn glx-btn-g" href="#glx-method">See it work</a>
            </div>
          </div>
        </section>

        <section className="glx-chap" data-chap="1" id="glx-method">
          <div className="glx-kick">01 — You speak</div>
          <div className="glx-prompt">"Start a Volume Bot on BONK with 0.5 SOL"<span className="cur">&nbsp;</span></div>
          <p className="glx-say">No dashboard. No code. Plain language, typed into a Claude.ai chat you already use.</p>
        </section>

        <section className="glx-chap" data-chap="2">
          <div className="glx-kick">02 — Claude reasons</div>
          <h2>It reads<br />the <em>intent.</em></h2>
          <p className="glx-say">Claude interprets your sentence, picks the right ICE_Man tool, and fills the parameters — token, amount, route.</p>
        </section>

        <section className="glx-chap" data-chap="3">
          <div className="glx-kick">03 — ICE_Man routes</div>
          <h2>Through <em>Jupiter.</em><br /><span className="stroke">Onto Solana.</span></h2>
          <p className="glx-say">The Model Context Protocol bridge signs the transaction and routes the swap through Jupiter V6 on Solana mainnet.</p>
        </section>

        <section className="glx-chap" data-chap="4">
          <div className="glx-kick">04 — Confirmed</div>
          <div className="glx-confirm">
            <div className="hd">ICE_Man · on-chain</div>
            <div className="r"><span style={{ color: "var(--t3)" }}>token</span><span>BONK</span></div>
            <div className="r"><span style={{ color: "var(--t3)" }}>amount</span><span>0.5 SOL</span></div>
            <div className="r"><span style={{ color: "var(--t3)" }}>route</span><span>Jupiter V6</span></div>
            <div className="r"><span style={{ color: "var(--t3)" }}>status</span><span className="ok">✓ Confirmed</span></div>
            <div className="r"><span style={{ color: "var(--t3)" }}>sig</span><span>4xK9…mN2p</span></div>
          </div>
          <p className="glx-say">Under 5 seconds. The confirmation lands back inside your chat. You never left the conversation.</p>
        </section>
      </div>

      {/* SOLID GROUND — translucent so the 3D flight stays visible underneath, all the way to the end */}
      <div className="glx-solid">
        <section id="glx-priority" className="glx-sec">
          <div className="glx-wrap">
            <div className="glx-sectop"><span className="n">05</span><h3>Priority spots <em>filling fast.</em></h3></div>
            <ScarcityCounter onClickJoin={function () { setPriorityOpen(true); }} />
          </div>
        </section>

        <VelvetRope />

        {/* Pricing */}
        <section className="glx-sec" id="glx-access">
          <div className="glx-wrap">
            <div className="glx-sectop"><span className="n">06</span><h3>Two ways <em>in.</em></h3></div>
            <div className="glx-tiers">
              <div className="glx-tier">
                <div className="pn">Harmony</div>
                <div className="pr">$149<small> /mo</small></div>
                <div className="desc">Automated DeFi for serious retail traders.</div>
                <div className="ft"><span className="ck">✓</span>Up to 6 simultaneous bots</div>
                <div className="ft"><span className="ck">✓</span>Volume · Sniper · Lure</div>
                <div className="ft"><span className="ck">✓</span>Jupiter V6 execution</div>
                <div className="ft"><span className="ck">✓</span>Real-time P&amp;L dashboard</div>
                <button className="glx-btn glx-btn-g cta" onClick={function () { goTier("tier_6"); }}>Choose Harmony</button>
              </div>
              <div className="glx-tier pop">
                <div className="badge">ICE_Man included</div>
                <div className="pn">Power</div>
                <div className="pr">$499<small> /mo</small></div>
                <div className="desc">MCP-native DeFi, driven by Claude.ai.</div>
                <div className="ft"><span className="ck">✓</span>Up to 10 simultaneous bots</div>
                <div className="ft"><span className="ck">✓</span>ICE_Man MCP — exclusive</div>
                <div className="ft"><span className="ck">✓</span>Natural-language trading</div>
                <div className="ft"><span className="ck">✓</span>Sub-50ms priority RPC</div>
                <button className="glx-btn glx-btn-p cta" onClick={function () { goTier("tier_9"); }}>Choose Power</button>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA — the flight lands here */}
        <section className="glx-sec glx-last" style={{ textAlign: "center", borderBottom: "none" }}>
          <div className="glx-wrap">
            <div className="glx-kick" style={{ marginBottom: "22px" }}>Get started</div>
            <h3 style={{ fontFamily: "var(--disp)", fontWeight: 400, fontSize: "clamp(48px,8vw,120px)", lineHeight: 0.92, letterSpacing: "-.03em", marginBottom: "34px" }}>
              Your AI is ready.<br /><em style={{ fontStyle: "italic", color: "var(--gold)" }}>Are you?</em>
            </h3>
            <div>
              <button className="glx-btn glx-btn-p" onClick={function () { goTier("tier_9"); }}>Enter the grid →</button>{" "}
              <Link className="glx-btn glx-btn-g" to="/apply-investor">Investor access</Link>
            </div>
          </div>
        </section>
      </div>

      {/* FOOTER */}
      <footer className="glx-foot">
        <div className="glx-wrap">
          <div className="glx-fgrid">
            <div>
              <Link to="/" className="glx-brand" style={{ color: "var(--t1)" }}>
                <svg width="26" height="26" viewBox="0 0 40 40" fill="none"><circle cx="20" cy="20" r="18" stroke="var(--gold)" strokeWidth="1.3" /><circle cx="20" cy="20" r="4" fill="var(--gold)" /></svg>
                <span className="nm">GauriLabs</span>
              </Link>
              <p className="glx-flede">MCP-native DeFi infrastructure on Solana.</p>
            </div>
            <div>
              <h4>Protocol</h4>
              <Link to="/iceman">ICE_Man</Link>
              <a href="#glx-method">Method</a>
              <a href="#glx-access">Pricing</a>
            </div>
            <div>
              <h4>Account</h4>
              <Link to="/login">Login</Link>
              <Link to="/dashboard">Dashboard</Link>
            </div>
            <div>
              <h4>Access</h4>
              <Link to="/apply-investor">Investor program</Link>
              <Link to="/apply-partner">Affiliate</Link>
            </div>
          </div>
          <div className="glx-fbot">
            <span>© 2026 Gaurisha Astro Services OPC Pvt. Ltd. · Mumbai · Jaipur</span>
            <span>Auto dark / light · system-aware</span>
          </div>
        </div>
      </footer>

      {/* mode toggle */}
      <button className="glx-mode" onClick={toggleMode} title="Toggle theme" aria-label="Toggle theme">
        {isDark ? (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
        ) : (
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
        )}
      </button>

      <PriorityCheckoutModal open={priorityOpen} onClose={function () { setPriorityOpen(false); }} />
    </div>
  );
}
